import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { IconButton } from '@/components/atoms/IconButton';
import { Badge } from '@/components/atoms/Badge';
import { Spinner } from '@/components/atoms/Spinner';
import { cn } from '@/utils/cn';
import { ROUTES } from '@/constants/routes';
import {
  getBackofficeFeedback,
  type BackofficeFeedback,
  type FeedbackCampaign,
} from '@/services/api/feedbackAdmin.api';
import styles from './FeedbackCampaignDetailPage.module.scss';

const PROGRESS_LABELS = ['Nada', 'Un poco', 'Bastante', 'Mucho'];
const EASE_LABELS = ['Muy difícil', 'Regular', 'Fácil', 'Muy fácil'];
const LEVEL_FIT_LABELS: Record<string, string> = {
  easy: 'Demasiado fácil',
  right: 'En su punto',
  hard: 'Demasiado difícil',
};
const FEATURE_LABELS: Record<string, string> = {
  stories: 'Historias',
  conversation: 'Conversación',
  vocabulary: 'Vocabulario',
  gamification: 'Gamificación',
  rewards: 'Recompensas',
};

function npsBand(nps: number): 'promoter' | 'passive' | 'detractor' {
  if (nps >= 9) return 'promoter';
  if (nps >= 7) return 'passive';
  return 'detractor';
}
function scaleLabel(value: number | null, labels: string[]): string | null {
  if (value == null || value < 1 || value > labels.length) return null;
  return labels[value - 1] ?? null;
}
function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}
type CampaignStatus = 'activa' | 'cerrada' | 'programada';
function campaignStatus(c: FeedbackCampaign): CampaignStatus {
  if (c.active) return 'activa';
  return c.endDate < Date.now() ? 'cerrada' : 'programada';
}
const STATUS_BADGE: Record<CampaignStatus, { label: string; variant: 'success' | 'default' | 'info' }> = {
  activa: { label: 'Activa', variant: 'success' },
  cerrada: { label: 'Cerrada', variant: 'default' },
  programada: { label: 'Programada', variant: 'info' },
};

export function FeedbackCampaignDetailPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [data, setData] = useState<BackofficeFeedback | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!campaignId) return;
    setIsLoading(true);
    getBackofficeFeedback(campaignId)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Error cargando el feedback')
      )
      .finally(() => setIsLoading(false));
  }, [campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  // La campaña cargada realmente (el backend cae a la activa/reciente si el id no existe).
  const campaign =
    data?.campaigns.find((c) => c.id === (data?.selectedCampaignId ?? campaignId)) ?? null;
  const status = campaign ? STATUS_BADGE[campaignStatus(campaign)] : null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <IconButton
          icon={ArrowLeft}
          onClick={() => navigate(ROUTES.BACKOFFICE.FEEDBACK)}
          aria-label="Volver a Feedback"
        />
      </div>

      <div>
        <div className={styles.titleRow}>
          <h1>{campaign?.name ?? 'Campaña'}</h1>
          {status && <Badge variant={status.variant}>{status.label}</Badge>}
        </div>
        {campaign && (
          <p className={styles.meta}>
            {formatDate(campaign.startDate)} – {formatDate(campaign.endDate)}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className={styles.center}>
          <Spinner />
        </div>
      ) : error ? (
        <div className={styles.center}>
          <p className={styles.error}>{error}</p>
        </div>
      ) : !data || data.total === 0 ? (
        <div className={styles.center}>
          <p>Aún no hay respuestas en esta campaña.</p>
        </div>
      ) : (
        <>
          <div className={styles.summary}>
            <div className={cn(styles.card, styles.nps)}>
              <span className={styles.nps__label}>NPS</span>
              <span className={styles.nps__score}>{data.nps.score}</span>
              <div className={styles.nps__bar} aria-hidden>
                {data.nps.promoterPct > 0 && (
                  <span className={cn(styles.nps__seg, styles['nps__seg--promoter'])} style={{ width: `${data.nps.promoterPct}%` }} />
                )}
                {data.nps.passivePct > 0 && (
                  <span className={cn(styles.nps__seg, styles['nps__seg--passive'])} style={{ width: `${data.nps.passivePct}%` }} />
                )}
                {data.nps.detractorPct > 0 && (
                  <span className={cn(styles.nps__seg, styles['nps__seg--detractor'])} style={{ width: `${data.nps.detractorPct}%` }} />
                )}
              </div>
              <div className={styles.nps__legend}>
                <span><b className={styles['dot--promoter']} /> Promotores {data.nps.promoters} ({data.nps.promoterPct}%)</span>
                <span><b className={styles['dot--passive']} /> Pasivos {data.nps.passives} ({data.nps.passivePct}%)</span>
                <span><b className={styles['dot--detractor']} /> Detractores {data.nps.detractors} ({data.nps.detractorPct}%)</span>
              </div>
            </div>

            <div className={styles.stats}>
              <div className={styles.card}>
                <span className={styles.stat__label}>Respuestas</span>
                <span className={styles.stat__value}>{data.total}</span>
              </div>
              <div className={styles.card}>
                <span className={styles.stat__label}>Progreso percibido</span>
                <span className={styles.stat__value}>{data.averages.progress != null ? `${data.averages.progress} / 4` : '—'}</span>
              </div>
              <div className={styles.card}>
                <span className={styles.stat__label}>Facilidad de uso</span>
                <span className={styles.stat__value}>{data.averages.easeOfUse != null ? `${data.averages.easeOfUse} / 4` : '—'}</span>
              </div>
            </div>
          </div>

          <div className={styles.dists}>
            <div className={styles.card}>
              <h2 className={styles.card__title}>Nivel de los retos</h2>
              <ul className={styles.dist}>
                {(['easy', 'right', 'hard'] as const).map((k) => (
                  <li key={k}><span>{LEVEL_FIT_LABELS[k]}</span><b>{data.levelFit[k]}</b></li>
                ))}
              </ul>
            </div>
            <div className={styles.card}>
              <h2 className={styles.card__title}>Lo que más aporta</h2>
              <ul className={styles.dist}>
                {(['stories', 'conversation', 'vocabulary', 'gamification', 'rewards'] as const).map((k) => (
                  <li key={k}><span>{FEATURE_LABELS[k]}</span><b>{data.topFeature[k]}</b></li>
                ))}
              </ul>
            </div>
          </div>

          <h2 className={styles.sectionTitle}>Respuestas ({data.responses.length})</h2>
          <ul className={styles.responses}>
            {data.responses.map((r) => {
              const band = npsBand(r.nps);
              const chips = [
                scaleLabel(r.progress, PROGRESS_LABELS) && `Progreso: ${scaleLabel(r.progress, PROGRESS_LABELS)}`,
                r.levelFit && `Nivel: ${LEVEL_FIT_LABELS[r.levelFit] ?? r.levelFit}`,
                scaleLabel(r.easeOfUse, EASE_LABELS) && `Facilidad: ${scaleLabel(r.easeOfUse, EASE_LABELS)}`,
                r.topFeatures.length > 0 && `Aporta: ${r.topFeatures.map((f) => FEATURE_LABELS[f] ?? f).join(', ')}`,
              ].filter(Boolean) as string[];
              return (
                <li key={r.id} className={styles.card}>
                  <div className={styles.response__head}>
                    <span className={cn(styles.response__nps, styles[`response__nps--${band}`])}>{r.nps}</span>
                    <span className={styles.response__name}>{r.name ?? 'Alumno'}</span>
                    <span className={styles.response__date}>{formatDate(r.createdAt)}</span>
                  </div>
                  {r.npsReason && (
                    <p className={styles.response__open}><span className={styles.response__openLabel}>Motivo:</span> {r.npsReason}</p>
                  )}
                  {r.improvement && (
                    <p className={styles.response__open}><span className={styles.response__openLabel}>Mejora:</span> {r.improvement}</p>
                  )}
                  {chips.length > 0 && (
                    <div className={styles.response__chips}>
                      {chips.map((chip) => <span key={chip} className={styles.chip}>{chip}</span>)}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

export default FeedbackCampaignDetailPage;
